import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '../../../components/ui/Button';
import PanelCard from '../../performance-operations/components/PanelCard';
import { Badge, Sub } from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import { fetchInsights, formatNumber, requestGoalUpdate } from '../config';

// E04 AI alanı (analiz §7): "güncel kanıtı olmayan veya planın gerisindeki hedefleri açıklar. Öneri: durum güncellemesi
// iste veya hedefi gözden geçir. Risk sinyali, son değer tarihi ve plan eğrisi olmadan tahmin üretmez; Veri yetersiz
// yazar." Açıklamalar KURAL TABANLIDIR (LLM yok): her madde kuralın sayısal kanıtını (gün, plan/gerçekleşen, fark) taşır.
const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px;
  align-items: center;
  padding: 10px 12px;
  margin-bottom: 8px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.bgColor};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

export function insightText(t, item, lang) {
  if (item.rule === 'behindPlan') {
    return t('performance.goalAnalytics.ai.behindPlan', {
      planned: formatNumber(item.plannedPct, lang),
      actual: formatNumber(item.actualPct, lang),
      gap: formatNumber(item.gapPts, lang),
    });
  }
  return t('performance.goalAnalytics.ai.staleEvidence', { days: item.daysSinceUpdate });
}

export default function GoalInsightsPanel({
  filters, goalFilters, onOpenGoal, refreshKey,
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [busyId, setBusyId] = useState(null);
  const key = JSON.stringify([filters, goalFilters]);
  const panel = usePanel(() => fetchInsights(filters, goalFilters), [key, refreshKey], !!filters.periodId);
  const d = panel.data;
  const insufficient = d && d.state === 'insufficientData';

  const requestUpdate = async (item) => {
    setBusyId(item.goalId);
    await requestGoalUpdate(filters, item.goalId);
    setBusyId(null);
  };

  return (
    <PanelCard
      panelKey="goalInsights"
      title={t('performance.goalAnalytics.panel.ai')}
      span={12}
      loading={panel.loading}
      error={panel.error}
      onRetry={panel.retry}
      empty={!!insufficient}
      emptyText={t('performance.goalAnalytics.ai.insufficientData')}
    >
      {d ? (
        <>
          <Summary data-role="insight-summary">
            <Badge>{t('performance.goalAnalytics.ai.eligible', { count: d.eligibleCount })}</Badge>
            <Badge $tone={d.staleCount > 0 ? 'danger' : undefined}>{t('performance.goalAnalytics.ai.staleCount', { count: d.staleCount, days: d.staleDays })}</Badge>
            <Badge $tone={d.behindPlanCount > 0 ? 'danger' : undefined}>{t('performance.goalAnalytics.ai.behindCount', { count: d.behindPlanCount })}</Badge>
            <Badge>{t('performance.goalAnalytics.ai.noPlanCount', { count: d.noPlanCount })}</Badge>
          </Summary>
          {d.withPlanCount === 0 ? <Sub>{t('performance.goalAnalytics.ai.noPlanNote')}</Sub> : null}
          {(d.items || []).length === 0 ? <Sub>{t('performance.goalAnalytics.ai.noFindings')}</Sub> : null}
          {(d.items || []).map((item) => (
            <Item key={`${item.rule}-${item.goalId}`} data-insight={item.rule}>
              <div>
                <div><strong>{item.goalTitle}</strong>{` · ${item.employeeName || '-'}`}</div>
                <Sub>{insightText(t, item, lang)}</Sub>
                <Sub>{t(`performance.goalAnalytics.ai.action.${item.action}Hint`)}</Sub>
              </div>
              <Actions>
                {item.action === 'requestUpdate' ? (
                  <Button variant="outline" onClick={() => requestUpdate(item)} loading={busyId === item.goalId} data-action="request-update">
                    {t('performance.goalAnalytics.ai.action.requestUpdate')}
                  </Button>
                ) : null}
                <Button variant="ghost" onClick={() => onOpenGoal?.(item)} data-action="review-goal">
                  {t('performance.goalAnalytics.ai.action.reviewGoal')}
                </Button>
              </Actions>
            </Item>
          ))}
        </>
      ) : null}
    </PanelCard>
  );
}
